export const isBlank = (value) => {
     if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

// WhatsApp template names must use only lowercase letters, numbers and underscores (no spaces).
export const TEMPLATE_NAME_REGEX = /^[a-z0-9_]+$/;
export const TEMPLATE_NAME_MAX_LENGTH = 512;

export const validateTemplateName = (name) => {
  const value = (name ?? "").trim();

  if (!value) {
    return "Template Name is required";
  }

  if (value.length > TEMPLATE_NAME_MAX_LENGTH) {
    return `Template Name must not exceed ${TEMPLATE_NAME_MAX_LENGTH} characters`;
  }

  if (!TEMPLATE_NAME_REGEX.test(value)) {
    return "Template Name can only contain lowercase letters, numbers and underscores (e.g. order_confirmation_1)";
  }

  return null;
};

export const validateRequiredFields = (fields) => {
  const errors = {};

  Object.entries(fields).forEach(([key, config]) => {
    const value = config.value;
    const label = config.label;

    if (isBlank(value)) {
      errors[key] = `${label} is required`;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const extractVariables = (text = "") => {
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];

  return matches.map(match =>
    parseInt(match.replace(/[{}]/g, ""), 10)
  );
};

export const validateHeaderVariables = (header) => {
  const errors = {};

  if (header.format !== "TEXT") {
    return errors;
  }
 if (isBlank(header.text)) {
  // Header text is null, undefined, empty, or only spaces
  errors.header ="Header text cannot be blank if it is selected";
  return errors;
}
  const vars = extractVariables(header.text);

  if (vars.length > 1) {
    errors.header =
      "Header can contain only one variable ({{1}})";
    return errors;
  }

  if (vars.length === 1 && vars[0] !== 1) {
    errors.header =
      "Header variable must be {{1}}";
    return errors;
  }

  const exampleCount =
    header.example?.header_text?.length || 0;

  if (vars.length === 1 && exampleCount !== 1) {
    errors.header =
      "Header example value is required for {{1}}";
  }

   if (vars.length === 0 && exampleCount > 0) {
    errors.header =
      "Header example value is not required if variable {{1}} is not present in header text";
  }

  return errors;
};

export const validateBodyVariables = (body) => {
  const errors = {};

  const vars = extractVariables(body.text);

  const uniqueVars = [...new Set(vars)].sort((a, b) => a - b);

  for (let i = 0; i < uniqueVars.length; i++) {
    if (uniqueVars[i] !== i + 1) {
      errors.body =
        "Body variables must be sequential: {{1}}, {{2}}, {{3}}...";
      return errors;
    }
  }

  const exampleCount =
    body.example?.body_text?.[0]?.length || 0;

  if (uniqueVars.length !== exampleCount) {
    errors.body =
      `Body contains ${uniqueVars.length} variable(s) but ${exampleCount} example value(s) provided`;
  }

  return errors;
};

export const validateButtonVariables_backup = (buttonComponent) => {
  const errors = {};

  buttonComponent.buttons.forEach((button, index) => {

    if (button.type !== "URL") {
      return;
    }

    const url = button.url?.trim() || "";

    // Required
    if (!url) {
      errors[`button_${index}`] =
        `URL Button ${index + 1} URL is required`;
      return;
    }

    // Variable validation
    const matches = url.match(/\{\{(\d+)\}\}/g) || [];

    if (matches.length > 1) {
      errors[`button_${index}`] =
        `URL Button ${index + 1} can contain only one variable {{1}}`;
      return;
    }

    if (matches.length === 1 && matches[0] !== "{{1}}") {
      errors[`button_${index}`] =
        `URL Button ${index + 1} variable must be {{1}}`;
      return;
    }

    // Replace variable before URL validation
    const testUrl = url.replace(/\{\{1\}\}/g, "sample");

    try {
      const parsed = new URL(testUrl);

      if (
        parsed.protocol !== "https:" &&
        parsed.protocol !== "http:"
      ) {
        throw new Error();
      }
    } catch {
      errors[`button_${index}`] =
        `URL Button ${index + 1} must be a valid URL (e.g. https://example.com)`;
    }
    if (url.includes("{{1}}") && !url.endsWith("{{1}}")) {
      errors[`button_${index}`] =
        `URL Button ${index + 1} variable {{1}} must be at the end of URL`;
      return;
  }
  });

  return errors;
};

export const validateButtonVariables = (buttonComponent) => {
  const errors = {};

  buttonComponent.buttons.forEach((button, index) => {

    const type = button.type?.trim();
    const text = button.text?.trim();
    const otpType = button.otp_type?.trim();
    const phoneNumber = button.phone_number?.trim();
    const url = button.url?.trim() || "";

    // If any button field is populated then type is mandatory
    if (
      !type &&
      (
        text ||
        otpType ||
        phoneNumber ||
        url
      )
    ) {
      errors[`button_${index}`] =
        `Button ${index + 1}: Type is required`;
      return;
    }

    // Nothing entered for this row
    if (!type) {
      return;
    }

    // Type selected -> text mandatory
    if (!text) {
      errors[`button_${index}`] =
        `Button ${index + 1}: Button Text is required`;
      return;
    }

    // OTP Validation
    if (type === "OTP") {

      if (!otpType) {
        errors[`button_${index}`] =
          `Button ${index + 1}: OTP Type is required`;
        return;
      }
    }

    // Phone Validation
    if (type === "PHONE_NUMBER") {

      if (!phoneNumber) {
        errors[`button_${index}`] =
          `Button ${index + 1}: Phone Number is required`;
        return;
      }
    }

    // URL Validation
    if (type === "URL") {

      if (!url) {
        errors[`button_${index}`] =
          `Button ${index + 1}: URL is required`;
        return;
      }

      const matches =
        url.match(/\{\{(\d+)\}\}/g) || [];

      if (matches.length > 1) {
        errors[`button_${index}`] =
          `Button ${index + 1}: URL can contain only one variable {{1}}`;
        return;
      }

      if (
        matches.length === 1 &&
        matches[0] !== "{{1}}"
      ) {
        errors[`button_${index}`] =
          `Button ${index + 1}: URL variable must be {{1}}`;
        return;
      }

      if (
        url.includes("{{1}}") &&
        !url.endsWith("{{1}}")
      ) {
        errors[`button_${index}`] =
          `Button ${index + 1}: URL variable {{1}} must be at the end`;
        return;
      }

      const testUrl =
        url.replace(/\{\{1\}\}/g, "sample");

      try {
        const parsed = new URL(testUrl);

        if (
          parsed.protocol !== "http:" &&
          parsed.protocol !== "https:"
        ) {
          throw new Error();
        }
      } catch {
        errors[`button_${index}`] =
          `Button ${index + 1}: Invalid URL`;
      }
    }

    // QUICK_REPLY
    if (type === "QUICK_REPLY") {
      // only text required
    }

  });

  return errors;
};

export const deepTrim = (obj) => {
  if (typeof obj === "string") {
    return obj.trim();
  }

  if (Array.isArray(obj)) {
    return obj.map(deepTrim);
  }

  if (
    obj &&
    typeof obj === "object" &&
    obj.constructor === Object
  ) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        deepTrim(value)
      ])
    );
  }

  return obj;
};